import { Modal } from "./Modals";
import type { ApiClient, CredentialStore, GardenSummary, PublicationInfo } from "../core/ApiClient";
import { ApiOfflineError } from "../core/ApiClient";
import type { SyncManager, SyncSnapshot } from "../core/SyncManager";
import type { GardenStorage } from "../core/Storage";
import type { SaveData } from "../types";

const STATE_LABEL: Record<SyncSnapshot["state"], string> = {
  disabled: "この配信ではサーバー同期は無効です",
  offline: "サーバーに接続できていません",
  idle: "同期済み",
  syncing: "同期中…",
  conflict: "別のデバイスと競合しています",
  locked: "セーブ保護のため停止中",
};

/** 1 ゲーム内日あたりのゲーム内分。進行度の表示にしか使わない。 */
const MINUTES_PER_GAME_DAY = 1440;

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/gu, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[character] ?? character);
}

export interface AccountModalOptions {
  api: ApiClient;
  sync: SyncManager;
  credentials: CredentialStore;
  storage: GardenStorage;
  getSave: () => SaveData;
  notify: (message: string, kind?: "success" | "warning" | "error" | "info") => void;
  /** いまの庭を残したまま、新しい庭を始める。 */
  onStartNewGarden: () => Promise<{ ok: boolean; message?: string }>;
  /** 別の庭へ切り替える（ローカルは退避してから再読み込みされる）。 */
  onSwitchGarden: (gardenId: string) => Promise<{ ok: boolean; message?: string }>;
  /** いま遊んでいる庭の時間を止める／進める。 */
  setTimePaused: (paused: boolean) => void;
  isTimePaused: () => boolean;
  /** オフライン中にカレンダーが進む上限（ゲーム内日数）。説明文に使う。 */
  offlineCalendarCapDays: number;
}

/**
 * 同期状態の確認・引き継ぎコード・庭の一覧・オープンガーデン公開をまとめた画面。
 * ログインを要求しない代わりに、引き継ぎコードがデバイス間移動の唯一の導線になる。
 */
export class AccountModal extends Modal {
  private publication: PublicationInfo = { published: false };
  private issuedCode: string | null = null;
  private busy = false;
  /** null は「まだ取得していない」。空配列（取得済みで 0 件）と区別する。 */
  private gardens: GardenSummary[] | null = null;
  private gardensError: string | null = null;

  constructor(private readonly options: AccountModalOptions) {
    super("account-modal", "☁️ 同期と公開");
  }

  private get api(): ApiClient {
    return this.options.api;
  }

  private get sync(): SyncManager {
    return this.options.sync;
  }

  private notify(message: string, kind?: "success" | "warning" | "error" | "info"): void {
    this.options.notify(message, kind);
  }

  async show(): Promise<void> {
    this.issuedCode = null;
    this.gardens = null;
    this.gardensError = null;
    this.render();
    this.open();
    await Promise.all([this.refreshPublication(), this.refreshGardens()]);
  }

  private async refreshPublication(): Promise<void> {
    const token = this.sync.token;
    const gardenId = this.sync.gardenId;
    if (!this.api.enabled || !token || !gardenId) return;
    try {
      this.publication = await this.api.getPublication(token, gardenId);
    } catch {
      this.publication = { published: false };
    }
    this.render();
  }

  private async refreshGardens(): Promise<void> {
    if (!this.api.enabled) return;
    try {
      this.gardens = await this.sync.listGardens();
      this.gardensError = null;
    } catch (error) {
      this.gardens = [];
      this.gardensError = error instanceof ApiOfflineError ? "サーバーに接続できないため、庭の一覧を取得できませんでした。" : "庭の一覧を取得できませんでした。";
    }
    this.render();
  }

  private render(): void {
    const status = this.sync.status;
    const lastSynced = status.lastSyncedAt ? new Date(status.lastSyncedAt).toLocaleString("ja-JP") : "—";
    const playerId = this.sync.playerId;

    this.body.innerHTML = `
      <section class="account-section">
        <h3>同期状態</h3>
        <p class="account-status account-status--${status.state}">${escapeHtml(STATE_LABEL[status.state])}</p>
        ${status.message ? `<p class="account-note">${escapeHtml(status.message)}</p>` : ""}
        <dl class="account-facts">
          <dt>最終同期</dt><dd>${escapeHtml(lastSynced)}</dd>
          <dt>プレイヤーID</dt><dd><code>${escapeHtml(playerId ? `${playerId.slice(0, 8)}…` : "未発行")}</code></dd>
        </dl>
        ${status.state === "conflict" ? '<button data-reload class="primary">最新の庭を読み込む（再読み込み）</button>' : ""}
      </section>

      ${this.api.enabled ? this.gardenListSection() : ""}
      ${this.api.enabled ? this.transferSection() : '<section class="account-section"><p class="account-note">このビルドは VITE_API_BASE_URL が未設定のため、セーブはこのブラウザ内にのみ保存されます。</p></section>'}
      ${this.api.enabled ? this.publishSection() : ""}
      ${this.restartSection()}
    `;

    this.body.querySelector("[data-reload]")?.addEventListener("click", () => window.location.reload());
    this.body.querySelector("[data-issue-code]")?.addEventListener("click", () => void this.issueCode());
    this.body.querySelector("[data-copy-code]")?.addEventListener("click", () => void this.copyCode());
    this.body.querySelector("[data-redeem]")?.addEventListener("click", () => void this.redeem());
    this.body.querySelector("[data-publish]")?.addEventListener("click", () => void this.publish());
    this.body.querySelector("[data-unpublish]")?.addEventListener("click", () => void this.unpublish());
    this.body.querySelector("[data-copy-url]")?.addEventListener("click", () => void this.copyUrl());
    this.body.querySelector("[data-restart]")?.addEventListener("click", () => void this.restart());

    this.body.querySelectorAll<HTMLElement>("[data-switch]").forEach((button) => button.addEventListener("click", () => void this.switchGarden(button.dataset.switch ?? "", button.dataset.name ?? "")));
    this.body.querySelectorAll<HTMLElement>("[data-rename]").forEach((button) => button.addEventListener("click", () => void this.rename(button.dataset.rename ?? "", button.dataset.name ?? "")));
    this.body.querySelectorAll<HTMLElement>("[data-toggle-time]").forEach((button) => button.addEventListener("click", () => void this.toggleTime(button.dataset.toggleTime ?? "", button.dataset.paused === "1")));
  }

  private gardenListSection(): string {
    const currentId = this.sync.gardenId;
    const cap = Math.max(0, Math.floor(this.options.offlineCalendarCapDays));
    const rows = this.gardens === null
      ? '<p class="account-note">読み込み中…</p>'
      : this.gardens.length === 0
        ? `<p class="account-note">${escapeHtml(this.gardensError ?? "サーバーに保存された庭はまだありません。")}</p>`
        : this.gardens.map((garden) => this.gardenRow(garden, garden.gardenId === currentId)).join("");

    return `
      <section class="account-section">
        <h3>🪴 庭の一覧</h3>
        <p class="account-note">同じプレイヤーで複数の庭を持てます。切り替えると、いまの庭はサーバーに保存されたまま残ります。<br>
        休止中の庭は離れているあいだも時間が進みません。進行中の庭は、次に開いたときに最大 ${cap} 日分までまとめて進みます。</p>
        <div class="garden-list">${rows}</div>
      </section>`;
  }

  private gardenRow(garden: GardenSummary, isCurrent: boolean): string {
    // いま遊んでいる庭は、サーバーの写しより手元の設定のほうが新しい。
    const paused = isCurrent ? this.options.isTimePaused() : garden.paused;
    const days = Math.max(1, Math.floor(garden.totalTicks / MINUTES_PER_GAME_DAY) + 1);
    const name = garden.name || "名前のない庭";
    const updated = new Date(garden.updatedAt).toLocaleString("ja-JP");
    const disabled = this.busy ? "disabled" : "";
    return `
      <article class="garden-row${isCurrent ? " garden-row--current" : ""}">
        <div class="garden-row-main">
          <h4>${escapeHtml(name)}${isCurrent ? '<span class="garden-badge">遊んでいる庭</span>' : ""}</h4>
          <p class="account-note">${days}日目 ・ 更新 ${escapeHtml(updated)}</p>
        </div>
        <div class="garden-row-actions">
          <button data-toggle-time="${escapeHtml(garden.gardenId)}" data-paused="${paused ? "1" : "0"}" class="garden-time${paused ? " garden-time--paused" : ""}" ${disabled}>${paused ? "‖ 休止中" : "▶ 進行中"}</button>
          <button data-rename="${escapeHtml(garden.gardenId)}" data-name="${escapeHtml(name)}" ${disabled}>名前</button>
          ${isCurrent ? "" : `<button data-switch="${escapeHtml(garden.gardenId)}" data-name="${escapeHtml(name)}" class="primary" ${disabled}>この庭で遊ぶ</button>`}
        </div>
      </article>`;
  }

  private transferSection(): string {
    return `
      <section class="account-section">
        <h3>別のデバイスへ引き継ぐ</h3>
        <p class="account-note">このデバイスで引き継ぎコードを発行し、移行先のデバイスで入力してください。コードは30分間有効で、1回だけ使えます。持っている庭はすべて引き継がれます。</p>
        ${this.issuedCode
          ? `<div class="transfer-code"><code>${escapeHtml(this.issuedCode)}</code><button data-copy-code>コピー</button></div>`
          : `<button data-issue-code ${this.busy ? "disabled" : ""}>引き継ぎコードを発行</button>`}
        <hr>
        <h3>このデバイスへ引き継ぐ</h3>
        <p class="account-note">移行元で発行したコードを入力します。<strong>このデバイスの現在の庭は置き換わります</strong>（退避スロットには残ります）。</p>
        <div class="transfer-input">
          <input data-code-input type="text" inputmode="latin" autocomplete="off" spellcheck="false" placeholder="XXXX-XXXX-XXXX" aria-label="引き継ぎコード">
          <button data-redeem ${this.busy ? "disabled" : ""}>引き継ぐ</button>
        </div>
      </section>`;
  }

  private publishSection(): string {
    const published = this.publication.published;
    return `
      <section class="account-section">
        <h3>🌼 オープンガーデン（読み取り専用公開）</h3>
        <p class="account-note">庭の見た目・植物・季節だけを公開します。所持GPや種の在庫、クエストの進み具合は公開されません。</p>
        <label>タイトル<input data-title type="text" maxlength="60" value="${escapeHtml(this.publication.title ?? "")}" placeholder="はじまりの小さな庭"></label>
        <label>ガーデナー名<input data-author type="text" maxlength="40" value="${escapeHtml(this.publication.authorName ?? "")}" placeholder="ななしのガーデナー"></label>
        <label>ひとこと<input data-description type="text" maxlength="240" value="${escapeHtml(this.publication.description ?? "")}" placeholder="秋の花壇がようやく揃いました"></label>
        ${published && this.publication.url
          ? `<div class="publication-url"><code>${escapeHtml(this.publication.url)}</code><button data-copy-url>コピー</button></div>
             <p class="account-note">閲覧数: ${Number(this.publication.viewCount ?? 0).toLocaleString()}</p>`
          : ""}
        <div class="modal-actions">
          ${published ? '<button data-unpublish>公開を停止</button>' : ""}
          <button data-publish class="primary" ${this.busy ? "disabled" : ""}>${published ? "公開内容を更新" : "この庭を公開する"}</button>
        </div>
      </section>`;
  }

  private restartSection(): string {
    const kept = this.api.enabled
      ? "いまの庭は<strong>消えません</strong>。サーバーに残るので、上の一覧からいつでも戻れます。"
      : "このビルドはサーバー同期が無効なため、<strong>いまの庭は戻せません</strong>。";
    return `
      <section class="account-section">
        <h3>🌱 新しい庭を始める</h3>
        <p class="account-note">6×6の何もない庭で、所持GP・クエスト・マスコットも最初の状態から始めます。${kept}</p>
        <button data-restart ${this.busy ? "disabled" : ""}>新しい庭を始める</button>
      </section>`;
  }

  private restart(): Promise<void> {
    return this.withBusy(async () => {
      const warning = this.api.enabled
        ? "新しい庭を始めます。いまの庭はサーバーに残るので、一覧から戻れます。\nよろしいですか？"
        : "新しい庭を始めます。サーバー同期が無効なため、いまの庭は元に戻せません。\nよろしいですか？";
      if (!window.confirm(warning)) return;
      const result = await this.options.onStartNewGarden();
      if (!result.ok) {
        this.notify(result.message ?? "新しい庭を始められませんでした。", "error");
        return;
      }
      this.notify("新しい庭を用意しています…", "success");
    });
  }

  private switchGarden(gardenId: string, name: string): Promise<void> {
    return this.withBusy(async () => {
      if (!gardenId) return;
      if (!window.confirm(`「${name}」に切り替えます。いまの庭はサーバーに保存されたまま残ります。\nよろしいですか？`)) return;
      const result = await this.options.onSwitchGarden(gardenId);
      if (!result.ok) {
        this.notify(result.message ?? "庭を切り替えられませんでした。", "error");
        return;
      }
      this.notify("庭を切り替えています…", "success");
    });
  }

  private rename(gardenId: string, current: string): Promise<void> {
    return this.withBusy(async () => {
      if (!gardenId) return;
      const input = window.prompt("庭の名前", current === "名前のない庭" ? "" : current);
      if (input === null) return;
      const name = input.trim().slice(0, 40);
      await this.sync.renameGarden(gardenId, name);
      await this.refreshGardens();
      this.notify("庭の名前を変更しました。", "success");
    });
  }

  private toggleTime(gardenId: string, paused: boolean): Promise<void> {
    return this.withBusy(async () => {
      if (!gardenId) return;
      const next = !paused;
      if (gardenId === this.sync.gardenId) {
        // 遊んでいる庭は手元の TimeManager が正。保存と同期はゲーム側が行う。
        this.options.setTimePaused(next);
        this.notify(next ? "この庭の時間を止めました。" : "この庭の時間を進めます。", "info");
        await this.refreshGardens();
        return;
      }
      const result = await this.sync.setGardenPaused(gardenId, next);
      if (!result.ok) {
        this.notify(result.message ?? "設定を変更できませんでした。", "error");
        return;
      }
      await this.refreshGardens();
      this.notify(next ? "この庭の時間を止めました。" : "この庭の時間を進めます。", "info");
    });
  }

  private async withBusy(task: () => Promise<void>): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    try {
      await task();
    } catch (error) {
      this.notify(error instanceof ApiOfflineError ? "サーバーに接続できませんでした。" : error instanceof Error ? error.message : "処理に失敗しました。", "error");
    } finally {
      this.busy = false;
      this.render();
    }
  }

  private issueCode(): Promise<void> {
    return this.withBusy(async () => {
      const token = this.sync.token;
      if (!token) throw new Error("同期がまだ開始されていません。");
      // コード発行前に最新状態を送っておかないと、移行先が古い庭を受け取ってしまう。
      await this.sync.pushNow();
      const issued = await this.api.createTransferCode(token);
      this.issuedCode = issued.code;
      this.notify("引き継ぎコードを発行しました。30分以内に移行先で入力してください。", "success");
    });
  }

  private async copyCode(): Promise<void> {
    if (!this.issuedCode) return;
    await navigator.clipboard.writeText(this.issuedCode).then(() => this.notify("コードをコピーしました。", "success"), () => this.notify("コピーできませんでした。", "warning"));
  }

  private async copyUrl(): Promise<void> {
    const url = this.publication.url;
    if (!url) return;
    await navigator.clipboard.writeText(url).then(() => this.notify("公開URLをコピーしました。", "success"), () => this.notify("コピーできませんでした。", "warning"));
  }

  private redeem(): Promise<void> {
    return this.withBusy(async () => {
      const input = this.body.querySelector<HTMLInputElement>("[data-code-input]");
      const code = input?.value.trim() ?? "";
      if (!code) throw new Error("引き継ぎコードを入力してください。");
      if (!window.confirm("このデバイスの現在の庭は、引き継いだ庭に置き換わります。よろしいですか？")) return;

      const redeemed = await this.api.redeemTransferCode(code);
      // 置き換わる側のセーブは退避スロットへ残し、取り返しがつかない状態にはしない。
      this.options.storage.stashConflict(this.options.getSave());
      this.options.credentials.write({ playerId: redeemed.playerId, token: redeemed.token, gardenId: null, syncedRevision: 0 });
      this.options.storage.clear();
      this.notify("引き継ぎました。庭を読み込み直します。", "success");
      window.setTimeout(() => window.location.reload(), 800);
    });
  }

  private publish(): Promise<void> {
    return this.withBusy(async () => {
      const token = this.sync.token;
      if (!token) throw new Error("同期がまだ開始されていません。");
      // 公開データはサーバー保存済みのセーブから作られるので、先に反映を確定させる。
      const pushed = await this.sync.pushNow();
      const gardenId = this.sync.gardenId;
      if (!pushed || !gardenId) throw new Error("サーバーへの反映に失敗したため公開できませんでした。");

      const meta = {
        title: this.body.querySelector<HTMLInputElement>("[data-title]")?.value ?? "",
        authorName: this.body.querySelector<HTMLInputElement>("[data-author]")?.value ?? "",
        description: this.body.querySelector<HTMLInputElement>("[data-description]")?.value ?? "",
      };
      const result = await this.api.publish(token, gardenId, meta);
      this.publication = { published: true, slug: result.slug, url: result.url, ...meta };
      this.notify("庭を公開しました。", "success");
    });
  }

  private unpublish(): Promise<void> {
    return this.withBusy(async () => {
      const token = this.sync.token;
      const gardenId = this.sync.gardenId;
      if (!token || !gardenId) return;
      await this.api.unpublish(token, gardenId);
      this.publication = { published: false };
      this.notify("公開を停止しました。", "info");
    });
  }
}
